/**
 * Subscription tier definitions — DEPRECATED compatibility shim.
 *
 * ## Status
 *
 * The authoritative tier config now lives in `./policy.ts` as
 * `TIER_POLICY`. The 47+ files that imported `TIER_DEFINITIONS` and the
 * `canX` helpers compile against this file during the policy-resolver
 * refactor; once every consumer migrates to `policy.ts` + the backend's
 * `WorkspacePolicyService`, this file is deleted entirely.
 *
 * ## What stays
 *
 * - `SubscriptionTier` + `BillingCyclePlan` string literals (used
 *   everywhere, no behaviour drift risk in re-exporting).
 * - `TIER_DEFINITIONS` derived from `TIER_POLICY` — preserves the legacy
 *   shape (`maxMembers`, `storageQuotaBytes`, `aiQuotas`, etc.) so existing
 *   imports keep type-checking.
 * - `TierDefinition` interface — legacy shape, kept in lockstep with what
 *   `TIER_DEFINITIONS` actually returns.
 * - `highestTier(...)` — used by `workspace.service` for owner-aggregate
 *   logic; not a policy concern.
 *
 * ## What was removed
 *
 * The eight legacy `canX` helpers (`canAddMember`, `canAddPaidMember`,
 * `canAddViewer`, `effectiveMaxViewers`, `canConsumeAiQuota`,
 * `aiQuotaSnapshotCapFor`, `effectiveStorageBytesFor`, `canConnectToolkit`,
 * `canCreateWorkspace`) and the per-tier predicates (`hasFeature`,
 * `isTierSufficient`, `nextTierAbove`, `phiEnabledFor`, etc.) are gone.
 * Callers go through `policyService.canX()` (one method per gate) or
 * import the new helpers from `policy.ts` directly:
 *
 *   - `policy.canUseFeature(workspaceId, 'phi')`           ← replaces phiEnabledFor / hasFeature
 *   - `policy.canConsumeAiQuota(workspaceId, ...)`         ← replaces canConsumeAiQuota / aiQuotaCapFor
 *   - `policy.canInviteMember(workspaceId, role)`          ← replaces canAddMember etc.
 *   - `policy.canUploadStorage(workspaceId, bytes)`        ← replaces effectiveStorageBytesFor checks
 *   - `tierAtLeast(current, minimum)` from policy.ts       ← replaces isTierSufficient
 *   - `tierFeatureKeys()` from policy.ts                   ← typed list of TierFeature keys
 *
 * If you need a helper that lived here and isn't in `policy.ts`, that's
 * a signal it shouldn't have been in shared at all — usually it belonged
 * in the backend policy resolver.
 */
import { TIER_POLICY, TIER_HIERARCHY, } from './policy';
const MARKETING = {
    FREE: {
        sellable: true,
        tagline: 'Start building your body of knowledge',
        description: 'For individuals getting started',
        pricing: { monthly: 0, annual: 0 },
        highlightedFeatures: [
            'You + unlimited guests · 50 GB storage',
            'Bower, your research partner (chat, search, summaries, auto-organize)',
            'Live agent mode with voice and video awareness',
            'Voice transcription and photo OCR (scientific vocab tuned)',
            'Personal AI memory · Workspace-shared AI instructions',
            'Rich editor with LaTeX, tables, diagrams, callouts and task lists',
            'Protocols, collections, tags and bidirectional linked entities',
            'Workspace visualizer (2D + 3D)',
            'Hybrid semantic + keyword search across everything you capture',
            'Mobile PWA with quick-capture shortcut · Export to PDF, Markdown, HTML',
            'Trash with 30-day restore',
            '7 days of version history · 30 days of audit history',
        ],
        recommended: false,
    },
    PRO: {
        sellable: false,
        tagline: 'Where your knowledge compounds',
        description: 'For researchers who need more',
        pricing: { monthly: 5900, annual: 4900 },
        highlightedFeatures: [
            'You + unlimited guests · 100 GB storage',
            '500 chats, 30 min live, 50 text extractions, 50 min voice / month',
            'Inferred AI context graph — Bower finds the links you did not draw',
            'Proactive confidence flags on captures',
            'Public share links',
            '"Just me" compliance lockdown',
            '1 year of version history · 1 year of audit history',
        ],
        // Not recommended, because it is not offered. Leaving the badge here left
        // the selector with no recommended card at all once PRO was hidden.
        recommended: false,
    },
    TEAM: {
        sellable: true,
        tagline: 'A shared system of record for your team',
        description: 'For labs and research groups',
        pricing: { monthly: 9900, annual: 7900 },
        highlightedFeatures: [
            'Paid seats + unlimited guests',
            '500 GB storage per paid seat, pooled',
            '1,000 chats, 60 min live, 100 text extractions, 100 min voice per paid seat / month',
            'Roles (Owner / Admin / Member / Viewer)',
            'Per-artifact privacy controls · Restricted mode (HIPAA-ready)',
            '7 years of version history · 7 years of audit history',
            'Priority support',
        ],
        recommended: true,
    },
};
// Translate the typed `Record<AiQuotaFeature, QuotaSpec>` to the legacy
// `AiQuotaSpec` shape (just the limit numbers).
function legacyAiQuotas(aiQuotas) {
    return {
        chat: aiQuotas.chat.limit,
        liveMinutes: aiQuotas.liveMinutes.limit,
        textExtractions: aiQuotas.textExtractions.limit,
        transcriptionMinutes: aiQuotas.transcriptionMinutes.limit,
        codeExecutionMinutes: aiQuotas.codeExecutionMinutes.limit,
    };
}
// Legacy `features: string[]` shape — derived from the new boolean Record.
// Each key that's true becomes a string in the array. Order is stable
// (Object.keys order) so any snapshot tests don't flake.
function legacyFeaturesArray(features) {
    const out = ['basic_notes', 'basic_search', 'basic_sharing'];
    if (features.phi)
        out.push('ai_chat', 'ocr', 'voice', 'bulk_export', 'public_sharing');
    // already pushed under phi block on PRO+ — guard against duplicates
    if (features.publicSharing && !out.includes('public_sharing'))
        out.push('public_sharing');
    if (features.prioritySupport)
        out.push('priority_support');
    return out;
}
// Maximum total members (legacy alias) = paid seats + effective max guests.
// For tiers where one is null (unlimited paid on TEAM, unlimited guests
// elsewhere) we fall back to a sentinel large number so legacy callers
// using `< maxMembers` don't false-positive — but they should be
// migrated to the policy resolver.
function legacyMaxMembers(policy) {
    if (policy.billing.maxSeats === null)
        return 10_000; // TEAM — policy resolver is authoritative
    const paid = policy.billing.maxSeats;
    const guests = policy.guests.maxTotal ?? paid * (policy.guests.perPaidSeat ?? 0);
    return paid + guests;
}
function legacyTierDefinition(policy) {
    const marketing = MARKETING[policy.tier];
    return {
        name: policy.displayName,
        tier: policy.tier,
        tagline: marketing.tagline,
        description: marketing.description,
        maxMembers: legacyMaxMembers(policy),
        storageQuotaBytes: policy.storage.bytesPerSeat,
        maxWorkspaces: null, // policy doesn't track this — always-true via support gate
        auditRetentionDays: policy.retention.auditDays,
        snapshotRetentionDays: policy.retention.snapshotDays,
        phiEnabled: policy.features.phi,
        maxConnectedToolkits: policy.connectors.maxPerUser,
        aiQuotas: legacyAiQuotas(policy.aiQuotas),
        features: legacyFeaturesArray(policy.features),
        pricing: marketing.pricing,
        highlightedFeatures: marketing.highlightedFeatures,
        recommended: marketing.recommended,
        sellable: marketing.sellable,
    };
}
export const TIER_DEFINITIONS = {
    FREE: legacyTierDefinition(TIER_POLICY.FREE),
    PRO: legacyTierDefinition(TIER_POLICY.PRO),
    TEAM: legacyTierDefinition(TIER_POLICY.TEAM),
};
// ---------------------------------------------------------------------------
// Helpers retained for cross-cutting callers.
// ---------------------------------------------------------------------------
export function formatPrice(cents) {
    if (cents === 0)
        return 'Free';
    return `$${(cents / 100).toFixed(0)}`;
}
/**
 * Given an array of tiers (e.g. the tiers of every workspace a user
 * owns), return the highest. Defaults to FREE if the list is empty.
 *
 * Stays in shared because `workspace.service.createWorkspace` needs it
 * before the policy resolver has a workspace to load.
 */
export function highestTier(tiers) {
    const ladder = ['FREE', 'PRO', 'TEAM'];
    let best = 'FREE';
    for (const t of tiers) {
        if (ladder.indexOf(t) > ladder.indexOf(best))
            best = t;
    }
    return best;
}
/**
 * The next tier above `tier` that is actually for sale, or `null` when there
 * is none.
 *
 * Upsell copy names a tier and then sends the user to `PlanSelector`, which
 * only renders tiers where `sellable` is true. So the obvious walk --
 * `TIER_HIERARCHY[TIER_HIERARCHY.indexOf(tier) + 1]` -- hands a FREE user the
 * retired PRO and produces "Upgrade to Individual" above a screen that refuses
 * to sell Individual. Two surfaces shipped exactly that walk (the version
 * history upsell and the connectors at-cap banner), which is why this is a
 * helper rather than a filter repeated at each call site.
 *
 * `null` means "render no upsell at all" -- the caller is already on the
 * highest sellable tier. Do not fall back to the raw next tier.
 */
export function nextSellableTierAbove(tier) {
    const start = TIER_HIERARCHY.indexOf(tier);
    if (start < 0)
        return null;
    for (const candidate of TIER_HIERARCHY.slice(start + 1)) {
        if (TIER_DEFINITIONS[candidate].sellable)
            return candidate;
    }
    return null;
}
/**
 * A retention window as upsell prose: "7 days" / "1 year" / "7 years".
 *
 * Takes `number`, NOT `number | null`, deliberately. An "unlimited" fragment
 * reads correctly on its own and ungrammatically in the sentence every caller
 * builds ("... for unlimited of version history"), so callers that can hold a
 * null window own that sentence themselves rather than passing null here. The
 * narrow signature makes the bad branch unrepresentable instead of merely
 * unused.
 *
 * The plural matters more than it looks: the two hand-rolled copies this
 * replaced both emitted "7 year", and neither could be caught by any other
 * check in the repo.
 */
export function formatRetentionWindow(days) {
    if (days >= 365) {
        const years = Math.round(days / 365);
        return `${years} year${years === 1 ? '' : 's'}`;
    }
    return `${days} day${days === 1 ? '' : 's'}`;
}
//# sourceMappingURL=tiers.js.map